import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { apiClient } from "@/lib/lutely-api/api-request";

export const Route = createFileRoute("/dev/_devLayout/dd-test")({
  component: DDTestPage,
});

interface EndpointResult {
  status?: number;
  data?: unknown;
  elapsed?: number;
  error?: string;
}

function useEndpointTest() {
  const [result, setResult] = useState<EndpointResult | null>(null);
  const [loading, setLoading] = useState(false);

  const run = async (path: string) => {
    setLoading(true);
    setResult(null);
    const start = Date.now();
    try {
      const res = await apiClient.get(path);
      setResult({
        status: res.status,
        data: res.data,
        elapsed: Date.now() - start,
      });
    } catch (err: unknown) {
      const elapsed = Date.now() - start;
      if (err && typeof err === "object" && "response" in err) {
        const axiosErr = err as { response: { status: number; data: unknown } };
        setResult({
          status: axiosErr.response.status,
          data: axiosErr.response.data,
          elapsed,
          error: "Request failed",
        });
      } else {
        setResult({
          error: err instanceof Error ? err.message : "Unknown error",
          elapsed,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return { result, loading, run };
}

function EndpointButton({
  label,
  path,
  description,
}: {
  label: string;
  path: string;
  description: string;
}) {
  const { result, loading, run } = useEndpointTest();
  const isError = result?.status && result.status >= 400;

  return (
    <div className="border border-zinc-700 rounded-lg p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <p className="text-sm text-zinc-400 mb-1">{description}</p>
          <code className="text-xs text-zinc-500">GET {path}</code>
        </div>
        <button
          onClick={() => run(path)}
          disabled={loading}
          className="shrink-0 rounded bg-zinc-700 px-4 py-2 text-sm font-medium text-white hover:bg-zinc-600 disabled:opacity-50"
          data-testid={`dd-test-${path.replace(/\//g, "-").slice(1)}`}
        >
          {loading ? "..." : label}
        </button>
      </div>
      {result && (
        <pre
          className={`mt-3 rounded p-3 text-xs overflow-auto max-h-48 ${
            isError
              ? "bg-red-950/50 text-red-300"
              : "bg-zinc-800/50 text-green-300"
          }`}
        >
          {`${result.status ?? "ERR"} (${result.elapsed}ms)\n${JSON.stringify(result.data, null, 2)}`}
        </pre>
      )}
    </div>
  );
}

function DDTestPage() {
  const env = import.meta.env.VITE_LOGICAL_ENV || "local";

  return (
    <div className="mx-auto max-w-2xl p-8">
      <h1 className="text-2xl font-bold text-white mb-2">
        Datadog Integration Test
      </h1>
      <p className="text-zinc-400 mb-8 text-sm">
        Exercise API endpoints to validate DD APM, Error Tracking, RUM, and
        logging. Environment: <code className="text-zinc-300">{env}</code>
      </p>

      <section className="mb-8">
        <h2 className="text-lg font-semibold text-zinc-200 mb-3">API Health</h2>
        <div className="space-y-3">
          <EndpointButton
            label="Healthcheck"
            path="/healthcheck"
            description="Basic health check — should return 200"
          />
          <EndpointButton
            label="Protected"
            path="/healthcheck/protected"
            description="Auth-protected health check — 401 if not logged in"
          />
        </div>
      </section>

      <section className="mb-8">
        <h2 className="text-lg font-semibold text-zinc-200 mb-3">
          Error Scenarios
        </h2>
        <div className="space-y-3">
          <EndpointButton
            label="500 Unhandled"
            path="/healthcheck/test/error/500"
            description="Throws raw Error — validates AllExceptionsFilter + DD span tagging"
          />
          <EndpointButton
            label="500 String"
            path="/healthcheck/test/error/http-string"
            description="HttpException with string body — validates response normalization"
          />
          <EndpointButton
            label="422 Object"
            path="/healthcheck/test/error/http-object"
            description="HttpException with object body — validates passthrough"
          />
          <EndpointButton
            label="404 Not Found"
            path="/healthcheck/test/error/not-found"
            description="NotFoundException — should NOT trigger error logging (4xx)"
          />
        </div>
      </section>

      <section className="mb-8">
        <h2 className="text-lg font-semibold text-zinc-200 mb-3">
          Observability
        </h2>
        <div className="space-y-3">
          <EndpointButton
            label="Emit Logs"
            path="/healthcheck/test/log"
            description="Emits log, warn, and error — check API console + DD Logs"
          />
          <EndpointButton
            label="Slow (3s)"
            path="/healthcheck/test/slow?ms=3000"
            description="3-second delay — validates APM trace duration"
          />
        </div>
      </section>
    </div>
  );
}
